import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CaretLeft, CaretRight, GearSix, List, Question } from '@phosphor-icons/react';
import { listMyOrganizations } from '@/api/orgs';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { NotificationBell } from '@/components/NotificationBell';
import { ChatHeaderButton } from '@/components/ChatHeaderButton';
import {
  GettingStartedDialog,
  GettingStartedLauncher,
} from '@/components/GettingStartedCard';
import { AICommandBar } from '@/components/AICommandBar';
import { SettingsPanel } from './SettingsPanel';
import { SidebarCollapsedContext } from './sidebar-context';
import { SidebarContent } from './SidebarContent';
import { TRACKABLE_NAV } from './nav-items';
import { useRecentNav } from './useRecentNav';

// DashboardShell is the chrome around every authenticated org page: a fixed
// sidebar on desktop, a sheet-driven drawer on mobile, and an <Outlet/> that
// renders the current page in the remaining space. The shell owns org-level
// data the inner pages can't fetch as efficiently (org name for the header)
// and exposes nothing to children — everything they need is on the route or
// in their own queries.
export function DashboardShell() {
  const { slug = '' } = useParams();
  const auth = useAuth();
  const { t } = useTranslation('common');
  const token = auth.user?.access_token ?? null;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Sidebar starts expanded on every page load; the toggle is session-local
  // (per product decision), so we deliberately don't persist this state.
  const [collapsed, setCollapsed] = useState(false);

  // Close the mobile drawer whenever the route changes. NavLink onClick
  // handlers in SidebarContent already do this when the user picks an item,
  // but this catch-all also covers programmatic redirects.
  useEffect(() => {
    setMobileOpen(false);
    setSettingsOpen(false);
  }, [location.pathname]);

  // Recent + pinned nav state lives at the shell level so the route-track
  // effect below and the SidebarNav reads share the same instance — two
  // useRecentNav() calls would have separate React state and only converge
  // on slug change.
  const recentNav = useRecentNav(slug);
  useEffect(() => {
    if (!slug) return;
    const suffix = location.pathname.replace(`/orgs/${slug}`, '');
    const item = TRACKABLE_NAV.find((i) => i.to === suffix);
    if (item) recentNav.push(item.id);
    // PRIMARY_NAV pages don't enter the recents list — they have a
    // permanent slot at the top of the sidebar. Anything else (member
    // detail pages, callbacks, etc.) is also ignored: only nav-modeled
    // routes (HUB with a `to` + DEEP) count as a "visited page" worth
    // surfacing. Action-only items (Settings hub) never match because
    // they have no `to`.
  }, [location.pathname, slug, recentNav]);

  // Org list is already cached by Landing; reuse the same query key so we
  // don't refetch when the user navigated here directly via a bookmark.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });

  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const orgName = org?.name ?? slug;
  const logoKey = org?.logo_object_key ?? '';

  // The URL slug points to an org the user is no longer in (DB wipe in dev,
  // removed as admin, ownership transferred away, zero orgs left). Bounce
  // back to landing once the list has actually loaded so the user lands on
  // the org picker or the empty state instead of a shell wrapped around a
  // 403-storm. 401s are handled separately by the apiFetch unauthorized
  // hook in main.tsx. Empty-list-and-on-/orgs/{slug} also falls into this
  // branch and correctly redirects.
  if (orgsQuery.isSuccess && !org) {
    return <Navigate to="/" replace />;
  }

  // Defense-in-depth: the admin SPA is owner/administrator-only — leaders work
  // from the mobile app. The /me/organizations filter already keeps leader orgs
  // out of the list (so the branch above catches them), but guard here too in
  // case that list is ever widened: never wrap the dashboard around a leader.
  if (org && org.role !== 'owner' && org.role !== 'administrator') {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarCollapsedContext.Provider value={collapsed}>
    <div className="flex min-h-dvh bg-muted/30">
      {/* Desktop sidebar — visible from `lg` (1024px) up. Below that it's a
          sheet triggered by the top-bar hamburger. Width animates between
          collapsed (icon rail) and expanded states.
          `lg:sticky lg:top-0 lg:h-dvh` pins it to viewport height — without
          that, flex stretches the aside to match a tall main column on long
          pages and the footer drops below the fold. */}
      <aside
        className={`relative hidden shrink-0 border-r border-border bg-background transition-[width] duration-200 ease-out lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col ${
          collapsed ? 'w-[72px]' : 'w-[260px]'
        }`}
      >
        <SidebarContent
          orgName={orgName}
          slug={slug}
          logoKey={logoKey}
          collapsed={collapsed}
          recentNav={recentNav}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {/* Collapse toggle — pinned to the right edge, vertically centered.
            The button straddles the border so it reads as part of the rail
            chrome rather than the page content. */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          className="absolute -right-3 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-pill border border-border bg-background text-foreground/70 shadow-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {collapsed ? (
            <CaretRight className="h-3.5 w-3.5" />
          ) : (
            <CaretLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </aside>

      {/* Mobile drawer — always full layout (collapse only applies to the
          desktop rail; the mobile sheet is already an overlay). hideClose:
          tapping anywhere outside the drawer already closes it, and the
          drawer's own org block carries enough chrome that an X icon was
          adding clutter rather than affordance. */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" hideClose className="w-[280px] p-0">
          <SheetTitle className="sr-only">{t('nav.label')}</SheetTitle>
          <SidebarContent
            orgName={orgName}
            slug={slug}
            logoKey={logoKey}
            collapsed={false}
            recentNav={recentNav}
            onOpenSettings={() => {
              setSettingsOpen(true);
              setMobileOpen(false);
            }}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar — mobile shows just the hamburger on the left; the
            opened drawer already carries org identity, so duplicating
            it here only crowds the bar and steals room from the AI
            command pill. Desktop has no top-bar org block either —
            the sidebar carries it. The cog on the right is the entry
            point to the settings panel and shows at every breakpoint.
            `h-16` (64px) matches the sidebar's org-block height so the
            bottom border lines up with the sidebar's separator regardless
            of the sidebar collapsed/expanded state. */}
        {/* The AI assistant no longer lives in the header — it's a floating
            launcher fixed to the bottom-right corner (mounted at the shell
            root below). The header is a simple flex bar: hamburger on the
            left (mobile), utility icons on the right. */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-background px-3">
          <div className="flex items-center justify-start">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('nav.openMenu')}
              onClick={() => setMobileOpen(true)}
              className="lg:hidden"
            >
              <List className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center justify-end gap-1">
            <GettingStartedLauncher slug={slug} />
            <ChatHeaderButton slug={slug} />
            <NotificationBell slug={slug} />
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('settings.openFeedback')}
              asChild
            >
              <Link to={`/orgs/${slug}/settings/feedback`}>
                <Question className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('settings.openPanel')}
              aria-haspopup="dialog"
              aria-expanded={settingsOpen}
              onClick={() => setSettingsOpen((s) => !s)}
            >
              <GearSix className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <SettingsPanel
        slug={slug}
        role={org?.role ?? ''}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />

      {/* Setup dialog lives at the shell level so the header launcher can open
          it from any page (via ?setup=1). sidebarCollapsed lets it center over
          the content area rather than the full viewport. */}
      <GettingStartedDialog slug={slug} sidebarCollapsed={collapsed} />

      {/* AI assistant — floating launcher in the bottom-right corner, available
          on every org page. Mounted once at the shell root so it also keeps
          servicing the `ai-command-bar:submit` event that settings pages fire
          to seed the assistant with a prompt. */}
      <AICommandBar slug={slug} variant="fab" />
    </div>
    </SidebarCollapsedContext.Provider>
  );
}
